import { useState } from 'react'
import { ActivityIndicator, Image, ScrollView } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigation, useRoute } from '@react-navigation/native'
import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import { Icon, colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Input } from '@zet/design-system/input'
import { Stack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import UserFilled from '@zet/design-system/svg/UserFilled'
import { B1, B2, B5, H4, SH3 } from '@zet/design-system/typography'
import useDsaStore from '@zet/feature-user/api/freshchat/store/dsa'
import storageUtil from '@zet/utils/storage'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useSaveUserDetails, useUpdateProfilePic } from '../../api/login/hooks'
import UploadProfilePicture from '../../components/login/uploadProfilePicture'
import { i18n } from '../../localization'
import useStore from '../../store'
import { EMAIL_REGEX, NAME_REGEX } from '../../utils/regularExpression'
import {
  EMAIL_FIELD_VALIDATION,
  NAME_FIELD_EMPTY,
  NAME_FIELD_VALIDATION,
} from '../../utils/validationMessage'

const validationSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, { message: NAME_FIELD_EMPTY })
    .regex(new RegExp(NAME_REGEX), NAME_FIELD_VALIDATION),
  email: z.preprocess(
    (arg) => (arg === '' ? undefined : arg),
    z
      .string()
      .trim()
      .regex(new RegExp(EMAIL_REGEX), EMAIL_FIELD_VALIDATION)
      .optional()
  ),
})

const UserDetails = () => {
  const [isUploadBottomSheetVisible, setIsUploadBottomSheetVisible] =
    useState(false)
  const { params } = useRoute()
  const navigation = useNavigation()
  const { user, setUser, setSelectedDsaId } = useStore((state) => state)
  const { setSelectedDsaId: setSelectedDsa } = useDsaStore((state) => state)
  const {
    isLoading,
    mutate,
    data: presignedProfilePicture,
  } = useUpdateProfilePic()
  const {
    mutate: saveUserDetailsMutate,
    isLoading: saveUserDetailsLoading,
    data,
  } = useSaveUserDetails()

  type ValidationSchema = z.infer<typeof validationSchema>
  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors, isValid },
  } = useForm<ValidationSchema>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues: {
      fullName: params?.selectedDsaData?.name,
      email: params?.selectedDsaData?.email,
    },
  })

  const onSubmit = () => {
    saveUserDetailsMutate(
      {
        name: getValues('fullName'),
        email: getValues('email') || '',
      },
      {
        onSuccess: async (data) => {
          if (data?.action === 'SHOW_BRIDGE_HOME_SCREEN') {
            setSelectedDsaId(params?.dsaId)
            setSelectedDsa(params?.dsaId)
            setUser({
              ...user,
              name: getValues('fullName'),
              profilePicUrl: presignedProfilePicture,
            })
            storageUtil.saveObject('token', params?.token)
            navigation.replace('SignupSuccess', {
              companyName: params?.dsaList?.[0]?.organisationName,
            })
          }
        },
      }
    )
  }

  return (
    <KeyBoardWrapper
      className=" flex-1 bg-white"
      BottomSheet={
        <Stack className=" border-t-grays-30 border-t px-3 py-[12]">
          <Button
            size="lg"
            className="w-full"
            disabled={!isValid}
            loading={saveUserDetailsLoading}
            onPress={() => onSubmit(handleSubmit)}
          >
            {i18n.t('verifyPhNum.confirm')}
          </Button>
        </Stack>
      }
    >
      <ScrollView className="mb-3 flex-1">
        <Stack className="pt-3">
          <H4 className="text-center">{i18n.t('userDetails.heading')}</H4>
        </Stack>
        <Stack className="h-14 self-center">
          <Image
            source={require('../../../assets/logo.png')}
            resizeMode="contain"
            style={{ height: 100, width: 200 }}
          />
        </Stack>
        <H4 className="mt-3 text-center">
          {params?.dsaList?.[0]?.organisationName}
        </H4>
        <SH3 className="text-grays-60 mx-3 mt-3 text-center">
          {i18n.t('userDetails.subHeading')}
        </SH3>

        <Pressable
          className="mx-3 mb-1 mt-3 flex-row"
          onPress={() => setIsUploadBottomSheetVisible(true)}
        >
          <Stack
            className="border-grays-40"
            style={{
              borderStyle: presignedProfilePicture ? 'solid' : 'dashed',
              borderWidth: 1,
              width: 120,
              height: 120,
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            {/* <Image
              source={{
                uri: 'https://i.pinimg.com/736x/d9/56/9b/d9569bbed4393e2ceb1af7ba64fdf86a.jpg',
              }}
              style={{ height: '100%' }}
            /> */}
            {presignedProfilePicture ? (
              <Image
                source={{
                  uri: presignedProfilePicture,
                }}
                style={{ height: '100%' }}
              />
            ) : isLoading ? (
              <Stack className="h-full items-center justify-center">
                <ActivityIndicator color={zetColors.primary[100]} />
              </Stack>
            ) : (
              <UserFilled size={120} color={zetColors.grays[50]} />
            )}
            <Stack
              className="bg-grays-30 absolute h-5 w-5 items-center justify-center rounded-md"
              style={{
                // top: '95%',
                bottom: '1%',
                right: '1%',
                borderColor: zetColors.neutral.white100,
                borderWidth: 2,
              }}
            >
              <Icon
                size="18"
                color={zetColors.grays[80]}
                className="p-1"
                type="EditFilled"
              />
            </Stack>
          </Stack>
          <Stack className="ml-2 justify-center">
            <Button
              className="w-full px-0"
              outlined
              textOnly
              textStyle="text-primary-100"
            >
              {i18n.t('userDetails.upload')}
              <B5 className=" text-grays-50">Maximum flie size: 2.5mb</B5>
            </Button>
          </Stack>
        </Pressable>

        <Stack className="mx-3 mb-1 mt-3">
          <B2 className="text-grays-80  ml-1">
            {i18n.t('userDetails.yourName')}
            <B1 className="text-error-100 ml-1">*</B1>
          </B2>
          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                placeholder="Eg: Ankush Singh"
                onChangeText={(value) => {
                  onChange(value)
                }}
                value={value}
                type={`${errors.fullName?.message ? 'error' : 'primary'}`}
                className=" mt-1 "
                onBlur={() => {
                  onBlur()
                }}
              />
            )}
            name="fullName"
          />
          {errors.fullName?.message ? (
            <Stack className="mt-2">
              <B5 className="text-error-100">{errors.fullName?.message}</B5>
            </Stack>
          ) : null}
        </Stack>

        <Stack className="mx-3 mb-1 mt-3">
          <B2 className="text-grays-80 ml-1">
            {i18n.t('userDetails.yourEmail')}
          </B2>
          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                placeholder="Eg: ankush.singh@gmail.com"
                onChangeText={(value) => {
                  onChange(value)
                }}
                value={value}
                type={`${errors.email?.message ? 'error' : 'primary'}`}
                className=" mt-1 "
                onBlur={() => {
                  onBlur()
                }}
              />
            )}
            name="email"
          />
          {errors.email?.message ? (
            <Stack className="mt-2">
              <B5 className="text-error-100">{errors.email?.message}</B5>
            </Stack>
          ) : null}
        </Stack>
      </ScrollView>
      <UploadProfilePicture
        isUploadBottomSheetVisible={isUploadBottomSheetVisible}
        setIsUploadBottomSheetVisible={setIsUploadBottomSheetVisible}
        mutate={mutate}
      />
    </KeyBoardWrapper>
  )
}

export default UserDetails
